/**
 * Responsive Design System - Breakpoints Configuration
 *
 * This system provides centralized breakpoint management for responsive design
 * Breakpoint ranges:
 * - 4K: 2560px - 3840px
 * - 2K: 1920px - 2559px
 * - FHD to MacBook: 1700px - 1919px (includes 16" MacBook at 1728px)
 * - Large: 1400px - 1699px
 * - Medium: 1200px - 1399px
 * - Small: below 1200px
 */

export const BREAKPOINTS = {
  // Minimum widths for each breakpoint
  fourK: 2560, // 4K displays
  twoK: 1920, // 2K/QHD displays
  fhdToMacbook: 1700, // FHD to MacBook Pro 16"
  large: 1400, // Large desktop
  medium: 1200, // Medium desktop
  small: 0, // Mobile/tablet
} as const;

// Viewport ranges
export const VIEWPORT_RANGES = {
  fourK: { min: BREAKPOINTS.fourK, max: 3840 },
  twoK: { min: BREAKPOINTS.twoK, max: BREAKPOINTS.fourK - 1 },
  fhdToMacbook: { min: BREAKPOINTS.fhdToMacbook, max: BREAKPOINTS.twoK - 1 },
  large: { min: BREAKPOINTS.large, max: BREAKPOINTS.fhdToMacbook - 1 },
  medium: { min: BREAKPOINTS.medium, max: BREAKPOINTS.large - 1 },
  small: { min: 0, max: BREAKPOINTS.medium - 1 },
} as const;

// Media query strings for CSS
export const MEDIA_QUERIES = {
  fourK: `(min-width: ${BREAKPOINTS.fourK}px)`,
  twoK: `(min-width: ${BREAKPOINTS.twoK}px) and (max-width: ${
    BREAKPOINTS.fourK - 1
  }px)`,
  fhdToMacbook: `(min-width: ${BREAKPOINTS.fhdToMacbook}px) and (max-width: ${
    BREAKPOINTS.twoK - 1
  }px)`,
  large: `(min-width: ${BREAKPOINTS.large}px) and (max-width: ${
    BREAKPOINTS.fhdToMacbook - 1
  }px)`,
  medium: `(min-width: ${BREAKPOINTS.medium}px) and (max-width: ${
    BREAKPOINTS.large - 1
  }px)`,
  small: `(max-width: ${BREAKPOINTS.medium - 1}px)`,

  // Additional useful queries
  upToFhdToMacbook: `(max-width: ${BREAKPOINTS.twoK - 1}px)`, // For MacBook and below
  upToLarge: `(max-width: ${BREAKPOINTS.fhdToMacbook - 1}px)`,
  upToMedium: `(max-width: ${BREAKPOINTS.large - 1}px)`,
} as const;

// Type for breakpoint names
export type Breakpoint = keyof typeof BREAKPOINTS;
export type ViewportRange = keyof typeof VIEWPORT_RANGES;

// Helper to get current breakpoint
export const getCurrentBreakpoint = (width: number): ViewportRange => {
  if (width >= BREAKPOINTS.fourK) return "fourK";
  if (width >= BREAKPOINTS.twoK) return "twoK";
  if (width >= BREAKPOINTS.fhdToMacbook) return "fhdToMacbook";
  if (width >= BREAKPOINTS.large) return "large";
  if (width >= BREAKPOINTS.medium) return "medium";
  return "small";
};

// Responsive values for different breakpoints
export type ResponsiveValue<T> = {
  fourK?: T;
  twoK?: T;
  fhdToMacbook?: T;
  large?: T;
  medium?: T;
  small?: T;
  default: T;
};

// Helper to get value for current breakpoint
export const getResponsiveValue = <T>(
  values: ResponsiveValue<T>,
  breakpoint: ViewportRange
): T => {
  return values[breakpoint] ?? values.default;
};
